/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import {
  ComposerNodeDescriptor,
  InputNodeDescriptor,
  NodeDescriptor,
  OutputNodeDescriptor,
} from "Api";
import {
  Edge,
  getNodesBounds,
  getViewportForBounds,
  Node,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { FileInfoPair, Path, PathType } from "../../../Api/DataTransformation";
import { NumberBucket } from "../../../Utils/Types";
import {
  createEntryForPath,
  createHandlesForEntry,
  modifyPathEndpoint,
} from "../../NodeDescriptorUtil";
import { toBlob } from "html-to-image";

export type TransformEngineNodeEntryId = number & {
  __type: "TransformEngineNodeEntryId";
};
export type TransformEngineNodeHandleId = number & {
  __type: "TransformEngineNodeHandleId";
};

/**
 * Handle id for the standalone "TRANSFORM ALL" parent handle on input nodes.
 * A connection whose sourceHandle equals this id represents full-file data;
 * any other source handle represents a single granular entry.
 */
export const PARENT_HANDLE_ID = "parent-handle-0";

export interface TransformEngineNodeEntry {
  id: TransformEngineNodeEntryId;
  label?: string;
  originalLabel: boolean;
  labelRange?: string;
  path: Path;
  isChild?: boolean;
}

export interface TransformEngineNodeHandle {
  id: TransformEngineNodeHandleId;
  entryId?: TransformEngineNodeEntryId;
  path: Path;
  handlePosition?: Position; // if provided, this will override the implied position from the path's pathType
}

export interface TransformEngineNodeData extends Record<string, unknown> {
  dataID: string; // this must be the same as the node ID
  title?: string;
  titleEditInProgress?: boolean;
  titleInProgress?: string;
  descriptor: NodeDescriptor | InputNodeDescriptor | OutputNodeDescriptor;
  entries: TransformEngineNodeEntry[];
  handles: TransformEngineNodeHandle[];
  mappingResult?: FileInfoPair | string;
}

/**
 * TODO: React also support "Sub Flows" which may be a suitable replacement for this
 * See https://reactflow.dev/docs/examples/sub-flows/
 */

export interface ReactFlowNode extends Node<TransformEngineNodeData> {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: TransformEngineNodeData;
  measured?: { width: number; height: number };
}

export type ReactFlowEdge = Edge<any>;

// ============================================================================
// HANDLE UTILITIES
// ============================================================================

/**
 * Returns the handle ID used in this string.
 */
export function extractHandleId(
  handleComponentKey: string
): TransformEngineNodeHandleId {
  const pos = handleComponentKey.indexOf("-handle-");
  if (pos >= 0) {
    return Number(
      handleComponentKey.substring(pos + 8)
    ) as TransformEngineNodeHandleId;
  } else {
    return undefined;
  }
}

/**
 * Check if a node title is valid
 * @param newTitle The title to validate
 * @returns Whether the title is valid
 */
export function isNodeTitleValid(newTitle: string): boolean {
  const re = /^[\w\-. ]+$/;
  return re.test(newTitle);
}

/**
 * Returns the node handle referenced by the ReactFlow handle string
 */
export function getHandleFromString(
  nodeData: TransformEngineNodeData,
  handleString: string
): TransformEngineNodeHandle {
  const handleId = extractHandleId(handleString);
  return findNodeHandle(nodeData, handleId);
}

/**
 * Get the position for a handle based on its path type
 */
export function getHandlePosition(
  handle: TransformEngineNodeHandle,
  pathType: PathType.INPUT | PathType.OUTPUT
): Position {
  return handle.handlePosition ?? pathType == PathType.INPUT
    ? Position.Left
    : Position.Right;
}

/**
 * Get the component key for a handle
 */
export function getHandleComponentKey(
  handlePosition: Position,
  handleId: TransformEngineNodeHandleId
): string {
  return `${handlePosition.toLowerCase()}-handle-${handleId}`;
}

/**
 * Find a node handle by ID
 */
export function findNodeHandle(
  nodeData: TransformEngineNodeData,
  handleId: TransformEngineNodeHandleId
): TransformEngineNodeHandle {
  const handle = nodeData.handles.find((handle) => handle.id == handleId);

  if (handle != null) {
    return handle;
  }
  return null;
}

/**
 * Find a node handle for a specific entry
 */
export function findNodeHandleForEntry(
  nodeData: TransformEngineNodeData,
  entryId: TransformEngineNodeEntryId
): TransformEngineNodeHandle {
  return nodeData.handles.find((handle) => handle.entryId == entryId);
}

// ============================================================================
// ENTRY UTILITIES
// ============================================================================

/**
 * Find a node entry by ID
 */
export function findNodeEntry(
  nodeData: TransformEngineNodeData,
  entryId: TransformEngineNodeEntryId
): TransformEngineNodeEntry {
  return nodeData.entries.find((entry) => entry.id == entryId);
}

export function getInputNodeAndPathsForComposer(
  edges: ReactFlowEdge[],
  nodes: ReactFlowNode[],
  targetNode: ReactFlowNode
): { pathGroups: Path[][]; sourceInputNode: InputNodeDescriptor } {
  const incomingEdges = edges.filter((edge) => edge.target === targetNode.id);

  // Collect path groups - each group represents one connection with its children
  const pathGroups: Path[][] = [];
  let sourceInputNode: InputNodeDescriptor = null;

  incomingEdges.forEach((edge) => {
    const inputNode = nodes.find((node) => node.data.dataID === edge.source);

    if (!inputNode) {
      return;
    }

    // Capture the source file type from the first input node
    if (sourceInputNode === null) {
      sourceInputNode = inputNode.data.descriptor as InputNodeDescriptor;
    }

    const entries: TransformEngineNodeEntry[] = inputNode.data.entries || [];

    // If this is a parent handle, it represents the whole node - include every
    // entry's path directly, no need to resolve a specific handle or filter by children.
    const isParentHandle = edge.sourceHandle?.startsWith("parent-handle");

    if (isParentHandle) {
      const connectionGroup: Path[] = entries.map((entry) => entry.path);
      pathGroups.push(connectionGroup);
      return;
    }

    // Parse the handle to get the connected entry
    const handle = getHandleFromString(inputNode.data, edge.sourceHandle);

    if (!handle) {
      return;
    }

    // Get the connected path
    const connectedPath = handle.path;
    const connectedPathEntries = connectedPath.pathEntries;

    // Create a new group for this connection
    const connectionGroup: Path[] = [];

    // Add the connected path itself
    connectionGroup.push(connectedPath);

    // Now find all entries in the input node that are children of this path
    entries.forEach((entry) => {
      const entryPathEntries = entry.path.pathEntries;

      // Check if this entry is a child of the connected path
      if (entryPathEntries.length > connectedPathEntries.length) {
        const isChild = connectedPathEntries.every((connectedEntry, index) => {
          return entryPathEntries[index]?.byString === connectedEntry.byString;
        });

        if (isChild) {
          connectionGroup.push(entry.path);
        }
      }
    });

    // Add this connection group to the list
    pathGroups.push(connectionGroup);
  });

  return { pathGroups, sourceInputNode };
}

/**
 * Get the component key for an entry
 */
export function getEntryComponentKey(
  entryId: TransformEngineNodeEntryId
): string {
  return `entry-${entryId}`;
}

// ============================================================================
// EDGE UTILITIES
// ============================================================================

/**
 * Check if an edge matches a specific node and optionally a handle
 */
export function edgeMatches(
  edge: ReactFlowEdge,
  nodeId: string,
  handleId?: TransformEngineNodeHandleId
): boolean {
  return (
    (edge.source &&
      edge.sourceHandle &&
      edge.source == nodeId &&
      (handleId === undefined ||
        extractHandleId(edge.sourceHandle) == handleId)) ||
    (edge.target &&
      edge.targetHandle &&
      edge.target == nodeId &&
      (handleId === undefined ||
        extractHandleId(edge.targetHandle) == handleId))
  );
}

/**
 * Returns whether this node is connected to an edge
 */
export function nodeHasEdge(
  allEdges: ReactFlowEdge[],
  nodeId: string
): boolean {
  return allEdges.some((edge) => edgeMatches(edge, nodeId));
}

// ============================================================================
// NODE DATA MODIFICATION UTILITIES
// ============================================================================

export function singleHandleHasEdge(
  allEdges: ReactFlowEdge[],
  nodeId: string,
  handleId: TransformEngineNodeHandleId
): boolean {
  return allEdges.some(
    (edge) =>
      edge.source &&
      edge.sourceHandle &&
      edge.source == nodeId &&
      (handleId === undefined || extractHandleId(edge.sourceHandle) == handleId)
  );
}

/**
 * Modify node data in a list of nodes
 */
export function modifyNodeData(
  existingNodes: ReactFlowNode[],
  modifiedNodeData: TransformEngineNodeData
): ReactFlowNode[] {
  return existingNodes.map((node: ReactFlowNode) => {
    return node.data.dataID == modifiedNodeData.dataID
      ? { ...node, data: modifiedNodeData }
      : node;
  });
}

/**
 * Modify output node data with a new descriptor
 */
export function modifyOutputNodeData(
  existingNodes: ReactFlowNode[],
  modifiedNodeData: TransformEngineNodeData,
  modifiedNodeDescriptor: OutputNodeDescriptor
): ReactFlowNode[] {
  return existingNodes.map((node: ReactFlowNode) => {
    return node.data.dataID == modifiedNodeData.dataID
      ? {
          ...node,
          data: { ...modifiedNodeData, descriptor: modifiedNodeDescriptor },
        }
      : node;
  });
}

export function modifyComposerNodeData(
  existingNodes: ReactFlowNode[],
  modifiedNodeData: TransformEngineNodeData,
  modifiedNodeDescriptor: ComposerNodeDescriptor
): ReactFlowNode[] {
  return existingNodes.map((node: ReactFlowNode) => {
    return node.data.dataID == modifiedNodeData.dataID
      ? {
          ...node,
          data: { ...modifiedNodeData, descriptor: modifiedNodeDescriptor },
        }
      : node;
  });
}

/**
 * Modify a specific node entry
 */
export function modifyNodeEntry(
  existingNodeData: TransformEngineNodeData,
  modifiedNodeEntry: TransformEngineNodeEntry
): TransformEngineNodeData {
  return {
    ...existingNodeData,
    entries: existingNodeData.entries.map((entry: TransformEngineNodeEntry) => {
      return entry.id == modifiedNodeEntry.id ? modifiedNodeEntry : entry;
    }),
  };
}

/**
 * Shift entries with path endpoint numbers greater than or equal to the reference entry
 * (but not the reference entry itself) by the specified delta.
 */
export function modifyPathEndpoints(
  _referenceEntry: TransformEngineNodeEntry,
  entries: TransformEngineNodeEntry[],
  _delta: number
): TransformEngineNodeEntry[] {
  return entries.map((entry) => {
    return entry;
  });
}

// ============================================================================
// ENTRY ADDITION UTILITIES
// ============================================================================

/**
 * Add a specific entry to a node (copying an existing entry)
 */
export function addSpecificEntry(
  oldNodeData: TransformEngineNodeData,
  entryId: TransformEngineNodeEntryId,
  delta: number
): [
  TransformEngineNodeData,
  TransformEngineNodeEntryId,
  TransformEngineNodeHandleId
] {
  const entryIdPosition = oldNodeData.entries.findIndex(
    (entry) => entry.id == entryId
  );
  const oldEntry = oldNodeData.entries[entryIdPosition];

  // get a new entry ID number that is bigger than all the existing IDs
  const newEntryIdNumber = oldNodeData.entries.reduce(
    (candidateIdNumber: number, entry: TransformEngineNodeEntry) => {
      return candidateIdNumber > (entry.id as number)
        ? candidateIdNumber
        : (entry.id as number) + 1;
    },
    1
  );

  // same with the handle
  const newHandleIdNumber = oldNodeData.handles.reduce(
    (candidateIdNumber: number, handle: TransformEngineNodeHandle) => {
      return candidateIdNumber > (handle.id as number)
        ? candidateIdNumber
        : (handle.id as number) + 1;
    },
    1
  );

  const number_regex = /[0-9]+$/;
  let newLabel = oldEntry.label ?? "";
  if (number_regex.test(newLabel)) {
    const number_string = newLabel.match(number_regex)[0];
    newLabel = newLabel.slice(0, -number_string.length);

    // get a label that doesn't collide with an existing entry
    let labelNumber = 1;
    let testLabel = "";
    // eslint-disable-next-line no-loops/no-loops
    while (true) {
      testLabel = newLabel + labelNumber.toString();
      if (
        oldNodeData.entries.findIndex(
          (entry) => testLabel == (entry.label ?? "")
        ) < 0
      ) {
        // success; no collision
        break;
      }
      labelNumber++;
    }
    newLabel = testLabel;
  } else {
    newLabel += "+";
  }

  // copy the old item, but give it a new ID and label, and reset the originalLabel flag
  // and also maybe adjust the new item's path endpoint
  const newEntry = {
    ...oldEntry,
    id: newEntryIdNumber as TransformEngineNodeEntryId,
    label: newLabel,
    originalLabel: true,
    path: delta == 0 ? oldEntry.path : modifyPathEndpoint(oldEntry.path, delta),
  };

  // insert the entry
  const firstHalf = oldNodeData.entries.slice(0, entryIdPosition + delta);
  const secondHalf = oldNodeData.entries.slice(entryIdPosition + delta);
  const entriesAfterAdd = firstHalf.concat(newEntry, ...secondHalf);

  // shift all the path endpoints for the entries that are similar to the entry we are adding
  const shiftedEntries = modifyPathEndpoints(newEntry, entriesAfterAdd, +1);

  // add new handles for the new entry
  const handleIdCounter: NumberBucket = { value: newHandleIdNumber };
  const newHandles = createHandlesForEntry(handleIdCounter, newEntry);
  const modifiedHandles = oldNodeData.handles.concat(newHandles);

  // find the one handle corresponding to the actual entry
  const newHandle = newHandles.find((handle) => handle.entryId == newEntry.id);

  const newNodeData = {
    ...oldNodeData,
    entries: shiftedEntries,
    handles: modifiedHandles,
  };

  return [newNodeData, newEntry.id, newHandle.id];
}

/**
 * Add a brand new entry to a node (from a path)
 */
export function addBrandNewEntry(
  oldNodeData: TransformEngineNodeData,
  path: Path
): [
  TransformEngineNodeData,
  TransformEngineNodeEntryId,
  TransformEngineNodeHandleId
] {
  const existingEntries = oldNodeData.entries;

  // get a new entry ID number that is bigger than all the existing IDs
  const newEntryIdNumber = oldNodeData.entries.reduce(
    (candidateIdNumber: number, entry: TransformEngineNodeEntry) => {
      return candidateIdNumber > (entry.id as number)
        ? candidateIdNumber
        : (entry.id as number) + 1;
    },
    1
  );

  // same with the handle
  const newHandleIdNumber = oldNodeData.handles.reduce(
    (candidateIdNumber: number, handle: TransformEngineNodeHandle) => {
      return candidateIdNumber > (handle.id as number)
        ? candidateIdNumber
        : (handle.id as number) + 1;
    },
    1
  );

  //create entry from scratch
  const newEntry = createEntryForPath(
    oldNodeData.descriptor,
    newEntryIdNumber,
    path
  );

  // add the entry
  const entriesAfterAdd = existingEntries.concat(newEntry);

  // add new handles for the new entry
  const handleIdCounter: NumberBucket = { value: newHandleIdNumber };
  const newHandles = createHandlesForEntry(handleIdCounter, newEntry);
  const modifiedHandles = oldNodeData.handles.concat(newHandles);

  // find the one handle corresponding to the actual entry
  const newHandle = newHandles.find((handle) => handle.entryId == newEntry.id);

  const newNodeData = {
    ...oldNodeData,
    entries: entriesAfterAdd,
    handles: modifiedHandles,
  };

  return [newNodeData, newEntry.id, newHandle.id];
}

const PADDING = 60;
const MAX_SIDE = 6000;

export async function exportCanvasToPng(
  container: HTMLElement,
  nodes: Node[],
  backgroundColor: string
): Promise<Blob | null> {
  const viewportEl = container.querySelector<HTMLElement>(
    ".react-flow__viewport"
  );
  if (!viewportEl || nodes.length === 0) return null;

  const bounds = getNodesBounds(nodes);
  const width = Math.min(Math.ceil(bounds.width) + PADDING * 2, MAX_SIDE);
  const height = Math.min(Math.ceil(bounds.height) + PADDING * 2, MAX_SIDE);
  const { x, y, zoom } = getViewportForBounds(
    bounds,
    width,
    height,
    0.1,
    2,
    0.05
  );

  return toBlob(viewportEl, {
    backgroundColor,
    width,
    height,
    pixelRatio: 2,
    style: {
      width: `${width}px`,
      height: `${height}px`,
      transform: `translate(${x}px, ${y}px) scale(${zoom})`,
    },
  });
}
